import { APP_ROUTE_MAP } from "./routes.constants";
import { TRouteName, TRoutes } from "./routes.types";

const prefixUrl = (pathname: string, app: TRouteName): string => {
  if (app === "thirdParty") {
    return pathname;
  }

  const baseUrl = APP_ROUTE_MAP[app];

  if (!baseUrl) {
    throw new Error(`No base URL configured for the "${app}" routes`);
  }

  return `${baseUrl?.replace(/\/+$/, "")}/${pathname.replace(/^\/+/, "")}`;
};

export const prefixRouteTree = <T extends TRoutes>(
  routeTree: T,
  app: TRouteName,
): T => Object.fromEntries(
  Object.entries(routeTree).map(([ name, route ]) => {
    if (typeof route === "function") {
      const routeFunction = route as (...args: unknown[]) => string;

      return [
        name,
        (...args: unknown[]) => prefixUrl(routeFunction(...args), app),
      ];
    }

    return [ name, prefixRouteTree(route, app) ];
  }),
) as T;


/**
 * `URL` requires an absolute base when parsing a relative path such as `/account`.
 * This reserved `.invalid` origin acts as a sentinel: after parsing, a safe relative
 * path must retain this exact origin. Inputs that normalize to another origin, such
 * as `/\evil.example`, are therefore rejected. The URL is never requested or resolved.
 */
const URL_VALIDATION_BASE = "https://local.invalid";

const isConfiguredOrigin = (url: URL): boolean =>
  Object.values(APP_ROUTE_MAP).some((configuredUrl) => {
    if (!configuredUrl) {
      return false;
    }

    try {
      return new URL(configuredUrl).origin === url.origin;
    } catch {
      return false;
    }
  });

export const isSafeInternalPath = (
  value: string | null | undefined,
): value is string => {
  if (!value) {
    return false;
  }

  try {
    if (value.startsWith("/") && !value.startsWith("//")) {
      return new URL(value, URL_VALIDATION_BASE).origin === URL_VALIDATION_BASE;
    }

    const url = new URL(value);

    return (url.protocol === "http:" || url.protocol === "https:")
      && isConfiguredOrigin(url);
  } catch {
    return false;
  }
};
